import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Box, 
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Avatar,
  useTheme,
  Menu,
  MenuItem,
  Typography,
  Divider
} from '@mui/material';
import mymaLogo from '../../assets/images/myma-logo-full-color.svg';
import { ChevronLeftOutlined, ChevronRightOutlined, Logout, KeyboardArrowDown, ExpandLess, ExpandMore, Close } from '@mui/icons-material';
import { navigationConfig, bottomNavigationConfig } from '../../config/navigation';
import { sidebarSelectedBg, sidebarHoverBg, sidebarText } from '../../themes/colors';
import { useAuth } from '../../context/AuthContext';
import LogoutDialog from '../Dialogs/LogoutDialog';
import { useTranslation } from 'react-i18next';

const drawerWidth = 207;
const collapsedDrawerWidth = 60;

const Sidebar = ({ activePage, onPageChange, activeTab = 0, onTabChange, isMobile = false, mobileOpen = false, onMobileClose = () => {} }) => {
  const { t } = useTranslation();
  const { currentUser, logout } = useAuth();
  const [collapsedState, setCollapsed] = useState(false);
  // On mobile the drawer is a full-width temporary overlay; never collapse it.
  const collapsed = isMobile ? false : collapsedState;
  const [, setIsTransitioning] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  const pathParts = location.pathname.split('/').filter(Boolean);
  const currentPath = pathParts[0] || 'insights';
  const currentTabSlug = pathParts[1] || null;
  const activePageName = activePage || currentPath;

  // Check if menu item should be expandable (has 2+ tabs)
  const isExpandable = (item) => item.tabs && item.tabs.length > 1;

  // Combine all nav items for lookup
  const allNavItems = [...navigationConfig, ...bottomNavigationConfig];

  // Auto-expand menu containing active page on load/page change
  React.useEffect(() => {
    const activeItem = allNavItems.find(item => item.id === activePageName);
    if (activeItem && isExpandable(activeItem)) {
      setExpandedMenus(prev => ({ ...prev, [activeItem.id]: true }));
    }
  }, [activePageName]);

  // Toggle menu expansion
  const toggleMenuExpand = (menuId) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuId]: !prev[menuId]
    }));
  };

  // Handle sub-item (tab) click — navigate to slug URL, tab derived from URL
  const handleTabClick = (parentItem, tabIndex) => {
    const tab = parentItem.tabs[tabIndex];
    const path = tab?.slug ? `${parentItem.path}/${tab.slug}` : parentItem.path;
    navigate(path);
    if (isMobile) onMobileClose();
  };

  // Get user initials for the avatar
  const getUserInitials = () => {
    if (!currentUser || !currentUser.name) return 'U';
    const names = currentUser.name.split(' ');
    if (names.length === 1) return names[0].charAt(0).toUpperCase();
    return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
  };
  
  // Get user's nickname for display
  const getNickname = () => {
    if (currentUser?.nickname) return currentUser.nickname;
    if (currentUser?.name) {
      // Fall back to first name if nickname is not available
      return currentUser.name.split(' ')[0];
    }
    return t('profile');
  };

  const toggleDrawer = () => {
    setIsTransitioning(true);
    setCollapsed(!collapsed);
    setTimeout(() => {
      setIsTransitioning(false);
    }, 400);
  };
  
  const handleNavigation = (item, event) => {
    if (item.id === 'preview') {
      // Handle profile menu click
      handleProfileMenuOpen(event);
      return;
    }

    // If item has 2+ tabs, toggle expand/collapse instead of navigating
    if (isExpandable(item)) {
      toggleMenuExpand(item.id);
      return;
    }

    // Only navigate, don't call onPageChange to avoid race condition
    navigate(item.path);
    if (isMobile) onMobileClose();
  };
  
  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };
  
  const handleLogoutClick = () => {
    handleProfileMenuClose();
    setLogoutDialogOpen(true);
  };
  
  const handleLogoutConfirm = () => {
    setLogoutDialogOpen(false);
    logout();
    navigate('/login');
  };

  const renderIcon = (item) => {
    if (item.isAvatar) {
      return (
        <Avatar 
          sx={{ 
            width: 24, 
            height: 24,
            bgcolor: theme.palette.secondary.main,
            color: theme.palette.secondary.contrastText,
            fontSize: '0.875rem',
            fontWeight: 'bold'
          }}
        >
          {getUserInitials()}
        </Avatar>
      );
    }
    const Icon = item.icon;
    return <Icon />;
  };

  return (
    <Drawer
      sx={{
        width: isMobile ? 0 : (collapsed ? collapsedDrawerWidth : drawerWidth),
        flexShrink: 0,
        transition: 'width 400ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
        overflow: 'hidden',
        '& .MuiDrawer-paper': {
          width: collapsed ? collapsedDrawerWidth : drawerWidth,
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          borderRight: 'none',
          transition: 'width 400ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
          overflowX: 'hidden',
        },
      }}
      variant={isMobile ? 'temporary' : 'permanent'}
      anchor="left"
      open={isMobile ? mobileOpen : true}
      onClose={onMobileClose}
      ModalProps={{ keepMounted: true }}
    >
      {/* Top section with logo and collapse button */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        padding: 2, 
        justifyContent: collapsed ? 'center' : 'space-between',
        minHeight: '56px'
      }}>
        {!collapsed && (
          <Box 
            component="img"
            src={mymaLogo}
            alt="Myma Logo"
            sx={{ height: '28px' }}
          />
        )}
        <IconButton
          onClick={isMobile ? onMobileClose : toggleDrawer}
          sx={{ color: '#fff' }}
          aria-label={isMobile ? t('close') : t('toggleMenu')}
        >
          {isMobile ? <Close /> : (collapsed ? <ChevronRightOutlined /> : <ChevronLeftOutlined />)}
        </IconButton>
      </Box>

      {/* Main menu items */}
      <List sx={{ paddingTop: 2 }}>
        {navigationConfig.map((item) => {
          const isSelected = activePageName === item.id;
          const isExpanded = expandedMenus[item.id];
          const hasExpandableItems = isExpandable(item);
          return (
            <React.Fragment key={item.id}>
              <ListItem disablePadding sx={{ marginBottom: hasExpandableItems && isExpanded ? '0px' : '8px' }}>
                <ListItemButton
                  onClick={(event) => handleNavigation(item, event)}
                  sx={{
                    position: 'relative',
                    backgroundColor: isSelected && !hasExpandableItems ? sidebarSelectedBg : 'transparent',
                    color: isSelected && !hasExpandableItems ? theme.palette.primary.main : sidebarText,
                    '&:hover': {
                      backgroundColor: isSelected && !hasExpandableItems ? sidebarSelectedBg : sidebarHoverBg,
                    },
                    borderRadius: '4px',
                    paddingLeft: '12px',
                    margin: '0 6px',
                    paddingTop: '8px',
                    paddingBottom: '8px',
                  }}
                >
                  <ListItemIcon sx={{
                    color: isSelected && !hasExpandableItems ? theme.palette.primary.main : theme.palette.primary.contrastText,
                    minWidth: '32px'
                  }}>
                    {renderIcon(item)}
                  </ListItemIcon>
                  {!collapsed && (
                    <>
                      <ListItemText
                        primary={t(item.titleKey || item.title.toLowerCase())}
                        primaryTypographyProps={{
                          sx: {
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            color: isSelected && !hasExpandableItems ? theme.palette.primary.main : sidebarText
                          }
                        }}
                      />
                      {hasExpandableItems && (
                        <Box sx={{ color: sidebarText, opacity: 0.7 }}>
                          {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                        </Box>
                      )}
                    </>
                  )}
                </ListItemButton>
              </ListItem>
              {/* Sub-items (tabs) when expanded */}
              {hasExpandableItems && isExpanded && !collapsed && (
                <List component="div" disablePadding sx={{ marginBottom: '8px' }}>
                  {item.tabs.map((tab, tabIndex) => {
                    const isTabSelected = isSelected && (tab.slug ? currentTabSlug === tab.slug : activeTab === tabIndex);
                    return (
                      <ListItemButton
                        key={tabIndex}
                        onClick={() => handleTabClick(item, tabIndex)}
                        sx={{
                          paddingLeft: '50px',
                          paddingY: '6px',
                          margin: '0 6px',
                          borderRadius: '4px',
                          backgroundColor: isTabSelected ? sidebarSelectedBg : 'transparent',
                          color: isTabSelected ? theme.palette.primary.main : sidebarText,
                          '&:hover': {
                            backgroundColor: isTabSelected ? sidebarSelectedBg : sidebarHoverBg,
                          },
                        }}
                      >
                        <ListItemText
                          primary={t(tab.labelKey || tab.label.toLowerCase())}
                          primaryTypographyProps={{
                            sx: {
                              fontSize: '0.8125rem',
                              fontWeight: isTabSelected ? 500 : 400,
                              color: isTabSelected ? theme.palette.primary.main : sidebarText
                            }
                          }}
                        />
                      </ListItemButton>
                    );
                  })}
                </List>
              )}
            </React.Fragment>
          );
        })}
      </List>

      {/* Bottom section */}
      <Box sx={{ marginTop: 'auto' }}>
        <List>
          {bottomNavigationConfig.map((item) => {
            const isSelected = activePageName === item.id;
            const isExpanded = expandedMenus[item.id];
            const hasExpandableItems = isExpandable(item);
            // Use nickname for profile item
            const displayTitle = item.id === 'preview' ? getNickname() : t(item.titleKey || item.title?.toLowerCase() || item.id);

            return (
              <React.Fragment key={item.id}>
                <ListItem disablePadding sx={{ marginBottom: hasExpandableItems && isExpanded ? '0px' : '8px' }}>
                  {item.id === 'preview' ? (
                    // Special styling for profile button
                    <ListItemButton
                      onClick={(event) => handleNavigation(item, event)}
                      sx={{
                        position: 'relative',
                        backgroundColor: 'transparent',
                        color: theme.palette.primary.main,
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        },
                        borderRadius: '8px',
                        margin: '0 6px',
                        padding: '6px 12px',
                        justifyContent: collapsed ? 'center' : 'flex-start',
                        border: '1px solid rgba(255, 255, 255, 0.9)',
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          color: 'white',
                          minWidth: collapsed ? '100%' : '32px',
                          justifyContent: collapsed ? 'center' : 'flex-start'
                        }}
                      >
                        {renderIcon(item)}
                      </ListItemIcon>
                      {!collapsed && (
                        <>
                          <ListItemText
                            primary={displayTitle}
                            primaryTypographyProps={{
                              sx: {
                                fontSize: '0.875rem',
                                fontWeight: 500,
                                color: 'white',
                              }
                            }}
                          />
                          <KeyboardArrowDown
                            fontSize="small"
                            sx={{
                              color: 'white',
                              opacity: 0.9
                            }}
                          />
                        </>
                      )}
                    </ListItemButton>
                  ) : (
                    // Regular styling for other buttons (Settings, etc.)
                    <ListItemButton
                      onClick={(event) => handleNavigation(item, event)}
                      sx={{
                        position: 'relative',
                        backgroundColor: isSelected && !hasExpandableItems ? sidebarSelectedBg : 'transparent',
                        color: isSelected && !hasExpandableItems ? theme.palette.primary.main : sidebarText,
                        '&:hover': {
                          backgroundColor: isSelected && !hasExpandableItems ? sidebarSelectedBg : sidebarHoverBg,
                        },
                        borderRadius: '4px',
                        paddingLeft: '12px',
                        margin: '0 6px',
                        paddingTop: '8px',
                        paddingBottom: '8px',
                      }}
                    >
                      <ListItemIcon sx={{
                        color: isSelected && !hasExpandableItems ? theme.palette.primary.main : theme.palette.primary.contrastText,
                        minWidth: '32px'
                      }}>
                        {renderIcon(item)}
                      </ListItemIcon>
                      {!collapsed && (
                        <>
                          <ListItemText
                            primary={displayTitle}
                            primaryTypographyProps={{
                              sx: {
                                fontSize: '0.875rem',
                                fontWeight: 500,
                                color: isSelected && !hasExpandableItems ? theme.palette.primary.main : sidebarText
                              }
                            }}
                          />
                          {hasExpandableItems && (
                            <Box sx={{ color: sidebarText, opacity: 0.7 }}>
                              {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                            </Box>
                          )}
                        </>
                      )}
                    </ListItemButton>
                  )}
                </ListItem>
                {/* Sub-items (tabs) when expanded */}
                {hasExpandableItems && isExpanded && !collapsed && (
                  <List component="div" disablePadding sx={{ marginBottom: '8px' }}>
                    {item.tabs.map((tab, tabIndex) => {
                      const isTabSelected = isSelected && (tab.slug ? currentTabSlug === tab.slug : activeTab === tabIndex);
                      return (
                        <ListItemButton
                          key={tabIndex}
                          onClick={() => handleTabClick(item, tabIndex)}
                          sx={{
                            paddingLeft: '50px',
                            paddingY: '6px',
                            margin: '0 6px',
                            borderRadius: '4px',
                            backgroundColor: isTabSelected ? sidebarSelectedBg : 'transparent',
                            color: isTabSelected ? theme.palette.primary.main : sidebarText,
                            '&:hover': {
                              backgroundColor: isTabSelected ? sidebarSelectedBg : sidebarHoverBg,
                            },
                          }}
                        >
                          <ListItemText
                            primary={t(tab.labelKey || tab.label.toLowerCase())}
                            primaryTypographyProps={{
                              sx: {
                                fontSize: '0.8125rem',
                                fontWeight: isTabSelected ? 500 : 400,
                                color: isTabSelected ? theme.palette.primary.main : sidebarText
                              }
                            }}
                          />
                        </ListItemButton>
                      );
                    })}
                  </List>
                )}
              </React.Fragment>
            );
          })}
        </List>
      </Box>
      
      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        PaperProps={{
          sx: { 
            boxShadow: 3,
            borderRadius: 1,
            minWidth: '200px'
          }
        }}
        anchorOrigin={{ horizontal: 'center', vertical: 'top' }}
        transformOrigin={{ horizontal: 'center', vertical: 'bottom' }}
      >
        {currentUser && (
          <>
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="subtitle1" fontWeight={500}>
                {currentUser.name || 'User'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {currentUser.email || ''}
              </Typography>
            </Box>
            <Divider />
          </>
        )}
        
        <MenuItem onClick={handleLogoutClick}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('sign_out')}</ListItemText>
        </MenuItem>
      </Menu>
      
      {/* Logout Confirmation Dialog */}
      <LogoutDialog
        open={logoutDialogOpen}
        onClose={() => setLogoutDialogOpen(false)}
        onConfirm={handleLogoutConfirm}
      />
    </Drawer>
  );
};

export default Sidebar;